import React, { useState, useRef, useEffect } from "react";
import styles from "./GroupPhotos.module.scss";
import TinySlider from "tiny-slider-react";
import sliderStyles from "./TinySlider.module.scss";
import SliderControls from "./SliderButton";
import { isMobile, isIOS } from "react-device-detect";
import { AzureMP } from "react-azure-mp";
import ReactPlayer from "react-player/lazy";
import { Context } from "../Store";
import CopiedContext from "../CopiedContext";
import ShareArrow from "./ShareArrow";
import { trackEvent } from "../utils/helpers";

const GroupPhotosNew = (props) => {
  const state = React.useContext(Context);
  const photosSliderRef = useRef();
  const loopcamsSliderRef = useRef();
  const hasTrackedPhotoScrollRef = useRef(false);
  const hasTrackedLoopcamScrollRef = useRef(false);
  const tabsRef = useRef(null);
  const tabSliderRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loopcamIndex, setLoopcamIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("photos");
  const [showCopied, setShowCopied] = useState(false);
  const copiedContextValue = { showCopied, setShowCopied };

  const photos = (state.groupPhotos || []).filter((p) => p && p.thumbnail_url);
  const loopcams = (state.loopcams || []).filter((v) => v && v.video_url);

  const hasPhotos = photos.length > 0;
  const hasLoopcams = loopcams.length > 0;
  const showTabs = hasPhotos && hasLoopcams;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentIndex(0);
  };

  const updateTabSlider = (tab) => {
    if (!tabsRef.current || !tabSliderRef.current) return;
    const activeBtn = tabsRef.current.querySelector(`[data-tab="${tab}"]`);
    if (!activeBtn) return;
    tabSliderRef.current.style.width = `${activeBtn.offsetWidth - 1}px`;
    tabSliderRef.current.style.transform = `translateX(${activeBtn.offsetLeft}px)`;
  };

  useEffect(() => {
    updateTabSlider(activeTab);
  }, [activeTab]);

  const showPhotosSlider = showTabs ? activeTab === "photos" : hasPhotos;
  const showLoopcamsSlider = showTabs ? activeTab === "loopcams" : hasLoopcams && !hasPhotos;

  // ── Photo data ──────────────────────────────────────────────────────────────

  const groupPhotoSources = photos.map((photo) => photo.thumbnail_url);
  const groupPhotoMedia = photos.map((photo) => ({
    mediaType: "PHOTO",
    mediaURI: photo.asset_uri,
    downloadURI: photo.thumbnail_url,
    sectionName: "Group Photo",
  }));

  const photoSettings = {
    lazyload: true,
    touch: true,
    nav: false,
    controls: false,
    mouseDrag: true,
    loop: photos.length >= 3,
    items: 1,
    gutter: 10,
    edgePadding: photos.length >= 2 && isMobile ? 30 : 0,
    responsive: {
      640: {
        items: photos.length === 2 ? 2 : 1,
        edgePadding: photos.length >= 3 ? 80 : 0,
        gutter: photos.length === 2 ? 14 : 10,
      },
      1024: {
        items: photos.length === 2 ? 2 : 1,
        edgePadding: photos.length >= 3 ? 280 : 0,
        gutter: 14,
      },
      1440: {
        items: photos.length >= 3 ? 3 : Math.min(photos.length, 2),
        edgePadding: photos.length >= 3 ? 60 : 0,
        gutter: 14,
      },
    },
  };

  // ── Loopcam data ──────────────────────────────────────────────────────────

  const showControls = isIOS;
  const loopcamVideos = loopcams.map((video) => {
    if (video.video_url?.includes(".mp4")) {
      return (
        <div className={styles.loopcamItem}>
          <ReactPlayer
            className="react-player"
            url={video.video_url}
            muted
            controls
            playing
            width="100%"
            height="100%"
          />
        </div>
      );
    } else {
      return (
        <div className={styles.loopcamItem}>
          <AzureMP
            options={{
              controls: showControls,
              autoplay: true,
              playsInline: true,
              muted: true,
            }}
            src={[{ src: video.video_url, type: "application/vnd.ms-sstr+xml" }]}
          />
        </div>
      );
    }
  });

  const loopcamMedia = loopcams.map((video) => ({
    mediaType: "VIDEO",
    mediaURI: video.asset_uri,
    mediaProvider: video.video_provider,
    videoURL: video.video_url,
    sectionName: "Loop Cams",
    thumbnail: video.thumbnail_url,
  }));

  const loopcamSettings = {
    lazyload: true,
    touch: true,
    nav: false,
    controls: false,
    mouseDrag: true,
    loop: loopcams.length >= 3,
    items: 1,
    gutter: 10,
    edgePadding: loopcams.length >= 2 && isMobile ? 30 : 0,
    responsive: {
      640: {
        items: loopcams.length === 2 ? 2 : 1,
        edgePadding: loopcams.length >= 3 ? 80 : 0,
        gutter: loopcams.length === 2 ? 14 : 10,
      },
      1024: {
        items: loopcams.length === 2 ? 2 : 1,
        edgePadding: loopcams.length >= 3 ? 280 : 0,
        gutter: 14,
      },
      1440: {
        items: loopcams.length >= 3 ? 3 : Math.min(loopcams.length, 2),
        edgePadding: loopcams.length >= 3 ? 60 : 0,
        gutter: 14,
      },
    },
  };

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!photosSliderRef.current) return;
    const slider = photosSliderRef.current.slider;
    const updatePosition = (info) => setCurrentIndex((info.displayIndex ?? info.index + 1) - 1);
    updatePosition(slider.getInfo());
    slider.events.on("indexChanged", updatePosition);
    const container = slider.getInfo().container;
    const handleCloneClick = (e) => {
      if (!e.target.closest(".tns-slide-cloned")) return;
      const target = e.target.closest("[data-photo-index]");
      if (!target) return;
      openPhotoLightbox(parseInt(target.getAttribute("data-photo-index"), 10));
    };
    container.addEventListener("click", handleCloneClick);
    return () => {
      if (slider?.events) slider.events.off("indexChanged", updatePosition);
      container.removeEventListener("click", handleCloneClick);
    };
  }, [photosSliderRef, showPhotosSlider]);

  useEffect(() => {
    if (!loopcamsSliderRef.current) return;
    const slider = loopcamsSliderRef.current.slider;
    if (!slider) return;
    const updatePosition = (info) => setLoopcamIndex((info.displayIndex ?? info.index + 1) - 1);
    updatePosition(slider.getInfo());
    slider.events.on("indexChanged", updatePosition);
    const container = slider.getInfo().container;
    const handleCloneClick = (e) => {
      if (!e.target.closest(".tns-slide-cloned")) return;
      const target = e.target.closest("[data-loopcam-index]");
      if (!target) return;
      openLoopcamLightbox(parseInt(target.getAttribute("data-loopcam-index"), 10));
    };
    container.addEventListener("click", handleCloneClick);
    return () => {
      if (slider?.events) slider.events.off("indexChanged", updatePosition);
      container.removeEventListener("click", handleCloneClick);
    };
  }, [loopcamsSliderRef, showLoopcamsSlider, activeTab]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const openPhotoLightbox = (index) => {
    props.openLightbox({
      section: "GroupPhoto",
      index,
      sources: groupPhotoSources,
      mediashare: groupPhotoMedia,
      type: "image",
    });
  };

  const handleLoopcamHover = (index, e) => {
    props.handleHover(
      {
        section: "LoopCams",
        index,
        ...loopcamMedia[index],
        target: e.currentTarget,
        timeStamp: e.timeStamp,
        groupLength: loopcamMedia.length,
        sources: loopcamVideos,
        mediashare: loopcamMedia,
      },
      e.type,
    );
  };

  const openLoopcamLightbox = (index) => {
    props.openLightbox({
      section: "LoopCams",
      index,
      sources: loopcamVideos,
      mediashare: loopcamMedia,
    });
  };

  const borderColors = ["var(--color-pink-100)", "var(--color-tan-100)", "var(--color-orange-100)"];

  if (!hasPhotos && !hasLoopcams) return null;

  return (
    <CopiedContext.Provider value={copiedContextValue}>
      <div className={`section scale-slider section--with-share-arrow ${styles.groupPhotosSection}`}>
        <h2>In the spotlight</h2>
        <h3>moments worth keeping</h3>
        {showTabs && (
          <div className={styles.tabsWrapper}>
            <div className={styles.tabs} ref={tabsRef}>
              <span className={styles.tabSlider} ref={tabSliderRef} />
              <button
                data-tab="photos"
                className={`${styles.tab}${activeTab === "photos" ? ` ${styles.tabActive}` : ""}`}
                onClick={() => handleTabChange("photos")}
              >
                Photos
              </button>
              <button
                data-tab="loopcams"
                className={`${styles.tab}${activeTab === "loopcams" ? ` ${styles.tabActive}` : ""}`}
                onClick={() => handleTabChange("loopcams")}
              >
                Loops
              </button>
            </div>
          </div>
        )}

        {hasPhotos && (
          <div
            className={
              showTabs && activeTab !== "photos"
                ? styles.tabContentHidden
                : !showTabs
                  ? styles.noTabsSpacing
                  : undefined
            }
          >
            <div
              className={`${styles.sliderContainer}${photos.length === 2 ? ` ${styles.sliderTwoUp}` : ""}`}
              style={
                photos.length === 1
                  ? { maxWidth: "711px", margin: "0 auto", padding: "0 22px" }
                  : !isMobile && photos.length === 2
                    ? { maxWidth: "1300px", margin: "0 auto" }
                    : undefined
              }
            >
              {!isMobile && photos.length >= 3 && <SliderControls slider={photosSliderRef} compact />}
              <TinySlider
                settings={photoSettings}
                ref={photosSliderRef}
                onIndexChanged={() => {
                  if (!hasTrackedPhotoScrollRef.current) {
                    hasTrackedPhotoScrollRef.current = true;
                    trackEvent("Gallery Scroll", "group_photo_scroll");
                  }
                }}
              >
                {photos.map((photo, index) => (
                  <div
                    key={index}
                    style={{ position: "relative" }}
                    className={[sliderStyles.slide, styles.groupPhotoItem].join(" ")}
                  >
                    <div
                      data-photo-index={index}
                      onClick={() => openPhotoLightbox(index)}
                      style={{
                        cursor: "pointer",
                        borderColor: borderColors[index % 3],
                      }}
                    >
                      <img src={photo.thumbnail_url} alt="" />
                    </div>
                    <ShareArrow
                      title="Group Photo"
                      url={photo.thumbnail_url}
                      text="Check out this group photo from our Electric Shuffle visit."
                      downloadUrl={photo.thumbnail_url}
                      isVideo={false}
                      customBottom="0px"
                      sectionName="Group Photo"
                      mediaShare={{
                        mediaType: "PHOTO",
                        mediaURI: photo.asset_uri,
                        sectionName: "Group Photo",
                        thumbnail: photo.thumbnail_url,
                      }}
                      cityurl={state.cityurl}
                      venue={state.venue}
                      azureurl={state.azureurl}
                      videourl={state.videourl}
                      guid={state.guid}
                      country={state.country}
                    />
                  </div>
                ))}
              </TinySlider>
            </div>
            {photos.length >= 3 && (
              <div className={styles.counterRow}>
                <button
                  className={styles.counterArrow}
                  onClick={() => photosSliderRef.current?.slider?.goTo("prev")}
                  aria-label="Previous"
                >
                  <img src="./img/slide-arrow-prev.svg" alt="" />
                </button>
                <div className={styles.slideCounter}>
                  <span>{currentIndex + 1}</span>/{photos.length}
                </div>
                <button
                  className={styles.counterArrow}
                  onClick={() => photosSliderRef.current?.slider?.goTo("next")}
                  aria-label="Next"
                >
                  <img src="./img/slide-arrow-next.svg" alt="" />
                </button>
              </div>
            )}
          </div>
        )}

        {hasLoopcams && (
          <div
            className={
              showTabs && activeTab !== "loopcams"
                ? styles.tabContentHidden
                : !showTabs
                  ? styles.noTabsSpacing
                  : undefined
            }
          >
            <div
              className={`${styles.sliderContainer}${loopcams.length === 2 ? ` ${styles.sliderTwoUp}` : ""}`}
              style={
                loopcams.length === 1
                  ? { maxWidth: "711px", margin: "0 auto", padding: "0 22px" }
                  : !isMobile && loopcams.length === 2
                    ? { maxWidth: "1300px", margin: "0 auto" }
                    : undefined
              }
            >
              {!isMobile && loopcams.length >= 3 && <SliderControls slider={loopcamsSliderRef} compact />}
              <TinySlider
                settings={loopcamSettings}
                ref={loopcamsSliderRef}
                onIndexChanged={() => {
                  if (!hasTrackedLoopcamScrollRef.current) {
                    hasTrackedLoopcamScrollRef.current = true;
                    trackEvent("Gallery Scroll", "loop_cam_scroll");
                  }
                }}
              >
                {loopcams.map((video, index) => (
                  <div
                    key={index}
                    style={{ position: "relative" }}
                    className={[sliderStyles.slide, styles.loopcamItem].join(" ")}
                  >
                    <div
                      data-loopcam-index={index}
                      onClick={() => openLoopcamLightbox(index)}
                      onMouseEnter={isMobile ? () => false : handleLoopcamHover.bind(this, index)}
                      onMouseLeave={handleLoopcamHover.bind(this, index)}
                      style={{
                        cursor: "pointer",
                        borderColor: borderColors[index % 3],
                      }}
                    >
                      {video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt="" />
                      ) : (
                        <img src={`${process.env.PUBLIC_URL}/img/Stories-Loop-Cam-graphic.png`} alt="" />
                      )}
                    </div>
                    <ShareArrow
                      title="Loop Cam"
                      url={video.video_url}
                      text="Check out this Loop Cam from our Electric Shuffle visit."
                      downloadUrl={video.video_url}
                      isVideo={true}
                      customBottom="0px"
                      sectionName="Loop Cams"
                      mediaShare={{
                        mediaType: "VIDEO",
                        mediaURI: video.asset_uri,
                        videoURL: video.video_url,
                        sectionName: "Loop Cams",
                        thumbnail: video.thumbnail_url,
                      }}
                      cityurl={state.cityurl}
                      venue={state.venue}
                      azureurl={state.azureurl}
                      videourl={state.videourl}
                      guid={state.guid}
                      country={state.country}
                    />
                  </div>
                ))}
              </TinySlider>
            </div>
            {loopcams.length >= 3 && (
              <div className={styles.counterRow}>
                <button
                  className={styles.counterArrow}
                  onClick={() => loopcamsSliderRef.current?.slider?.goTo("prev")}
                  aria-label="Previous"
                >
                  <img src="./img/slide-arrow-prev.svg" alt="" />
                </button>
                <div className={styles.slideCounter}>
                  <span>{loopcamIndex + 1}</span>/{loopcams.length}
                </div>
                <button
                  className={styles.counterArrow}
                  onClick={() => loopcamsSliderRef.current?.slider?.goTo("next")}
                  aria-label="Next"
                >
                  <img src="./img/slide-arrow-next.svg" alt="" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </CopiedContext.Provider>
  );
};

export default GroupPhotosNew;
